import React from 'react'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { faClose, FaIcon } from '@/lib/fa'
import DayCellSecond from '@/assets/images_new/legend/Day_Cell_Second.png'
import DayCell from '@/assets/images_new/legend/Day_Cell.png'
import Annex from '@/assets/images_new/legend/Annex.png'
import ContractStart from '@/assets/images_new/legend/Contract_Start.png'
import ContractEnd from '@/assets/images_new/legend/Contract_End.png'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useDayTypePalette } from '@/modules/drivers/stores/dayTypePaletteStore'
import {
  DAY_TYPE_FAMILIES,
  DAY_TYPE_FAMILY_LABELS,
  DAY_TYPE_PALETTE_OPTIONS,
  LEGEND_FAMILY_ORDER,
  getDayTypeVisual,
} from './charts/yearly/palettes/dayTypePalettes'
import { DayType } from '@/app/constants/dayType'

type Props = {
  handleClose: () => void
}

export const YearlyLegendModal = ({ handleClose }: Props) => {
  const { t, tSafe } = useLocalize()
  const paletteId = useDayTypePalette()
  const paletteLabel =
    DAY_TYPE_PALETTE_OPTIONS.find((o) => o.id === paletteId)?.label ?? ''
  return (
    <ModalLayout>
      <div className="tw-relative tw-flex tw-h-fit tw-max-h-[90vh] tw-w-full tw-max-w-[800px] tw-flex-col tw-overflow-y-auto tw-rounded-lg tw-bg-white tw-shadow-2xl">
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-p-4">
          <div className="tw-flex tw-items-center tw-gap-4">
            <p className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base">
              {t('driverModule.legend')}
            </p>
          </div>
          <FaIcon
            icon={faClose}
            className="tw-mr-4 tw-h-5 tw-w-5 tw-cursor-pointer tw-text-content-base hover:tw-text-primary"
            onClick={() => handleClose()}
          />
        </div>
        <div className="tw-grid tw-grid-cols-2">
          <SectionBlock image={DayCell} className="tw-border-b tw-border-base-300 tw-border-r">
            <div className="tw-flex tw-flex-col tw-gap-2">
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-h-4 tw-w-4 tw-bg-[#F7F9FF]"></div>
                <p className="tw-text-sm tw-text-content-base">{t('driverModule.yearly-legend-no-data')}</p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-h-4 tw-w-4 tw-bg-[#31CE55]"></div>
                <p className="tw-text-sm tw-text-content-base">
                  {t('driverModule.yearly-legend-digital-data')}
                </p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-h-4 tw-w-4 tw-bg-[#F7DC06]"></div>
                <p className="tw-text-sm tw-text-content-base">
                  {t('driverModule.yearly-legend-analog-data')}
                </p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-h-4 tw-w-4 tw-bg-[#404040]"></div>
                <p className="tw-text-sm tw-text-content-base">
                  {t('driverModule.yearly-legend-tachograph-data')}
                </p>
              </div>
            </div>
          </SectionBlock>
          <SectionBlock image={DayCellSecond} className="tw-border-b tw-border-base-300">
            {' '}
            <div className="tw-flex tw-flex-col tw-gap-2">
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-h-4 tw-w-4 tw-rounded-full tw-bg-[#3E8300]"></div>
                <p className="tw-text-sm tw-text-content-base">{t('driverModule.yearly-legend-gps-data')}</p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-h-4 tw-w-4 tw-rounded-full tw-bg-[#EA3718]"></div>
                <p className="tw-text-sm tw-text-content-base">{t('driverModule.yearly-legend-violations')}</p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-h-4 tw-w-4 tw-rounded-full tw-bg-[#FF7700]"></div>
                <p className="tw-text-sm tw-text-content-base">{t('driverModule.yearly-legend-loading')}</p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-h-4 tw-w-4 tw-rounded-full tw-bg-[#0095FF]"></div>
                <p className="tw-text-sm tw-text-content-base">{t('driverModule.yearly-legend-unloading')}</p>
              </div>
              <div className="tw-flex tw-items-start tw-gap-2">
                <div className="tw-mt-0.5 tw-h-4 tw-w-4 tw-flex-shrink-0 tw-rounded-full tw-bg-[#9333EA]"></div>
                <p className="tw-text-sm tw-text-content-base">{t('driverModule.yearly-legend-vehicle-source')}</p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-h-4 tw-w-4 tw-rounded-full tw-bg-[#0D9488]"></div>
                <p className="tw-text-sm tw-text-content-base">{t('driverModule.yearly-legend-tachograph-events')}</p>
              </div>
            </div>
          </SectionBlock>
          <SectionBlock image={Annex} className="tw-border-b tw-border-r tw-border-base-300">
            <div>
              <p className="tw-text-sm tw-text-content-base">{t('driverModule.yearly-legend-annex-start')}</p>
            </div>
          </SectionBlock>
          <SectionBlock image={ContractStart} className="tw-border-b tw-border-base-300">
            <div>
              <p className="tw-text-sm tw-text-content-base">{t('driverModule.yearly-legend-employment-start')}</p>
            </div>
          </SectionBlock>
          <SectionBlock image={ContractEnd} className='tw-border-r tw-border-base-300'>
            <div>
              <p className="tw-text-sm tw-text-content-base">{t('driverModule.yearly-legend-employment-end')}</p>
            </div>
          </SectionBlock>
          <SectionBlock
            visual={
              <div
                className="tw-h-12 tw-w-12 tw-flex-shrink-0"
                style={{ border: '2px solid #2c57ff' }}
              />
            }
          >
            <div>
              <p className="tw-text-sm tw-text-content-base">
                {tSafe(
                  'driverModule.yearly-legend-attestation',
                  'Dzień objęty wystawionym zaświadczeniem',
                )}
              </p>
            </div>
          </SectionBlock>
        </div>
        <DayTypeLegend paletteId={paletteId} paletteLabel={paletteLabel} />
      </div>
    </ModalLayout>
  )
}


const DayTypeLegend = ({
  paletteId,
  paletteLabel,
}: {
  paletteId: ReturnType<typeof useDayTypePalette>
  paletteLabel: string
}) => (
  <div className="tw-border-t tw-border-base-300 tw-px-6 tw-py-5">
    <p className="tw-m-0 tw-mb-3 tw-text-sm tw-font-semibold tw-text-content-base">
      Rodzaje dnia
      {paletteLabel && (
        <span className="tw-ml-2 tw-text-xs tw-font-normal tw-text-neutral">
          paleta: {paletteLabel}
        </span>
      )}
    </p>
    <div className="tw-grid tw-grid-cols-2 tw-gap-x-8 tw-gap-y-2 md:tw-grid-cols-3">
      {LEGEND_FAMILY_ORDER.map((family) => {
        const dayType = DAY_TYPE_FAMILIES[family][0]
        const visual = getDayTypeVisual(paletteId, dayType)
        return (
          <div key={family} className="tw-flex tw-items-center tw-gap-2">
            <span
              className="tw-grid tw-h-5 tw-w-8 tw-flex-shrink-0 tw-place-items-center tw-text-[11px] tw-font-semibold"
              style={{
                background: visual.background,
                color: visual.text,
                borderLeft: `${visual.borderWidth}px solid ${visual.border}`,
              }}
            >
              {DayType[dayType]}
            </span>
            <p className="tw-m-0 tw-text-xs tw-leading-snug tw-text-content-base">
              {DAY_TYPE_FAMILY_LABELS[family]}
            </p>
          </div>
        )
      })}
    </div>
  </div>
)

const SectionBlock = ({
  image = null,
  children,
  className = '',
  visual = null,
}: {
  image?: any
  children: React.ReactNode
  className?: string
  visual?: React.ReactNode
}) => {
  return (
    <div className={`tw-flex tw-gap-6 tw-px-6 tw-py-8 ${className}`}>
      {visual ?? <img src={image} alt="Day Cell" />}
      <div>{children}</div>
    </div>
  )
}
